/**
 * @file 后台顶栏用户菜单
 * @description 头像触发下拉：个人信息 / 修改密码 / 退出登录。
 *
 * @whenToUse AdminShell 顶栏右侧用户入口。
 * @whenNotToUse 表格行操作菜单（用 DropdownMenu 直接组合）；登录页。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminPasswordDialog } from '@/components/admin-password-dialog.tsx'
import { AdminProfileDialog } from '@/components/admin-profile-dialog.tsx'
import { Avatar } from '@/components/ui/avatar.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { clearAuthToken } from '@/lib/auth.ts'
import { logoutApi } from '@/api/auth.ts'
import { useUserStore } from '@/stores/user-store.ts'

export type AdminUserMenuProps = {
  /** 当前登录用户名 */
  username: string
  /** 头像图片地址；缺省展示用户名首字 */
  avatarSrc?: string
  /** 校区名称（个人信息弹窗） */
  campusName?: string
  /** 点数（个人信息弹窗） */
  points?: number
}

/**
 * 取用户名首字作为头像回退；英文字母转大写。
 * @param username 用户名
 * @returns 单个字符，空用户名时为 `?`
 */
export function getAvatarInitial(username: string): string {
  const ch = username.trim().charAt(0)
  return ch ? ch.toUpperCase() : '?'
}

/**
 * 顶栏用户头像 + 下拉菜单。
 */
export function AdminUserMenu({
  username,
  avatarSrc,
  campusName = '思犇优学总校',
  points = 0,
}: AdminUserMenuProps) {
  const navigate = useNavigate()
  const clearUser = useUserStore((s) => s.clearUser)
  const initial = getAvatarInitial(username)
  const [profileOpen, setProfileOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)

  return (
    <>
      <DropdownMenu
        align="end"
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full transition-opacity duration-200 hover:opacity-90"
            aria-label={`${username} 的用户菜单`}
          >
            <Avatar
              src={avatarSrc}
              alt={username}
              fallback={initial}
              className="cursor-pointer"
            />
            <span className="text-sm">{username}</span>
          </button>
        }
        items={[
          {
            key: 'profile',
            label: '个人信息',
            onSelect: () => setProfileOpen(true),
          },
          {
            key: 'password',
            label: '修改密码',
            onSelect: () => setPasswordOpen(true),
          },
          { key: 'divider', type: 'divider' },
          {
            key: 'logout',
            label: '退出登录',
            danger: true,
            onSelect: () => {
              logoutApi()
              clearAuthToken()
              clearUser()
              toast.success('已退出登录')
              navigate('/login', { replace: true })
            },
          },
        ]}
      />

      <AdminProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        username={username}
        campusName={campusName}
        points={points}
      />

      <AdminPasswordDialog
        open={passwordOpen}
        onOpenChange={setPasswordOpen}
      />
    </>
  )
}
